import * as yup from 'yup'
import {
  ApplePayMerchantCapabilities,
  ApplePaySupportedNetworks,
  GooglePayAuthMethods,
  GooglePayCardNetworks,
} from '@/common/models/groups'
import { PAYMENT_PROVIDER } from '../GroupsTab'

// festivalId is auto-derived from the festival being edited (not a user-entered
// field), so it stays optional here.
export const applePayConfigSchema = yup.object().shape({
  configId: yup.string().optional().nullable(),
  festivalId: yup.string().optional().nullable(),
  countryCode: yup.string().required('Country Code is required'),
  merchantIdentifier: yup.string().required('Merchant Identifier is required'),
  displayName: yup.string().required('Display Name is required'),
  supportedNetworks: yup
    .array()
    .of(yup.string().oneOf(Object.values(ApplePaySupportedNetworks)).required())
    .min(1, 'At least one network is required')
    .required(),
  merchantCapabilities: yup
    .array()
    .of(
      yup
        .string()
        .oneOf(Object.values(ApplePayMerchantCapabilities))
        .required(),
    )
    .min(1, 'At least one capability is required')
    .required(),
  stripePublishableKey: yup.string().optional().nullable(),
})

export const googlePayConfigSchema = yup.object().shape({
  configId: yup.string().optional().nullable(),
  festivalId: yup.string().optional().nullable(),
  countryCode: yup.string().required('Country Code is required'),
  environment: yup.string().required('Environment is required'),
  gateway: yup.string().required('Gateway is required'),
  merchantName: yup.string().optional().nullable(),
  merchantId: yup.string().optional().nullable(),
  cardNetworks: yup
    .array()
    .of(yup.string().oneOf(Object.values(GooglePayCardNetworks)).required())
    .min(1, 'At least one card network is required')
    .required(),
  authMethods: yup
    .array()
    .of(yup.string().oneOf(Object.values(GooglePayAuthMethods)).required())
    .min(1, 'At least one auth method is required')
    .required(),
  stripeVersion: yup.string().optional().nullable(),
  stripePublishableKey: yup.string().optional().nullable(),
})

export const paymentProvidersSchema = yup.object().shape({
  paymentProviders: yup
    .array()
    .of(
      yup.object().shape({
        backendId: yup.string().optional(),
        id: yup.string().optional(),
        title: yup.string().required('Title is required'),
        paymentProvider: yup
          .string()
          .oneOf([
            'BT_IPAY',
            'STRIPE',
            'CRYPTO_PULSAR',
            'NOW_PAYMENTS',
            'EU_PLATESC',
          ] as PAYMENT_PROVIDER[])
          .required('Payment provider is required'),
        // Stripe — optional, secret is not returned by backend
        apiSecret: yup.string().optional().nullable(),
        // BT_IPAY
        username: yup
          .string()
          .optional()
          .when('paymentProvider', {
            is: (val: PAYMENT_PROVIDER) => val === 'BT_IPAY',
            then: (s) => s.required('Username is required'),
            otherwise: (s) => s.optional().nullable(),
          }),
        password: yup
          .string()
          .optional()
          .when(['paymentProvider', 'saved'], {
            is: (val: PAYMENT_PROVIDER, saved: boolean) =>
              saved && val === 'BT_IPAY',
            then: (s) => s.required('Password is required'),
            otherwise: (s) => s.optional().nullable(),
          }),
        verificationKey: yup
          .string()
          .optional()
          .when(['paymentProvider', 'saved'], {
            is: (val: PAYMENT_PROVIDER, saved: boolean) =>
              saved && val === 'BT_IPAY',
            then: (s) => s.required('Verification Key is required'),
            otherwise: (s) => s.optional().nullable(),
          }),
        // Pulsar
        chains: yup
          .array()
          .of(
            yup.object().shape({
              chainName: yup.string().required('Chain Name is required'),
              chainId: yup.string().required('Chain ID is required'),
              taxPercent: yup.string().required('Tax Percent is required'),
            }),
          )
          .optional()
          .when('paymentProvider', {
            is: (val: PAYMENT_PROVIDER) => val === 'CRYPTO_PULSAR',
            then: (s) => s.min(1, 'At least one chain is required').required(),
            otherwise: (s) => s.optional().nullable(),
          }),
        tokens: yup
          .array()
          .of(
            yup.object().shape({
              tokenSymbol: yup.string().required('Token Symbol is required'),
              exchangeRate: yup.string().required('Exchange Rate is required'),
            }),
          )
          .optional()
          .when('paymentProvider', {
            is: (val: PAYMENT_PROVIDER) => val === 'CRYPTO_PULSAR',
            then: (s) => s.min(1, 'At least one token is required').required(),
            otherwise: (s) => s.optional().nullable(),
          }),
        saved: yup.boolean().optional(),
      }),
    )
    .required(),
})
